import React, { useState, useEffect } from 'react';
import { collection, getDocs, query, orderBy, where } from 'firebase/firestore';
import { db } from '../firebase';
import { Download, Filter, Calendar, Users, Building } from 'lucide-react';

const FarmRecordsExport = ({ onClose }) => {
  const [farmers, setFarmers] = useState([]);
  const [flops, setFlops] = useState([]);
  const [selectedFarmer, setSelectedFarmer] = useState('');
  const [selectedFlop, setSelectedFlop] = useState('');
  const [selectedMonth, setSelectedMonth] = useState('');
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear().toString());
  const [loading, setLoading] = useState(false);
  const [generatingPDF, setGeneratingPDF] = useState(false);
  const [records, setRecords] = useState([]);
  const [statistics, setStatistics] = useState({
    total: {},
    average: {},
    minimum: {},
    maximum: {}
  });
  const [showTable, setShowTable] = useState(false);

  useEffect(() => {
    fetchFarmers();
    fetchFlops();
  }, []);

  useEffect(() => {
    if (selectedFarmer || selectedFlop || selectedMonth || selectedYear) {
      fetchFilteredRecords();
      setShowTable(true);
    } else {
      setShowTable(false);
      setRecords([]);
      setStatistics({
        total: {},
        average: {},
        minimum: {},
        maximum: {}
      });
    }
  }, [selectedFarmer, selectedFlop, selectedMonth, selectedYear]);

  const fetchFarmers = async () => {
    try {
      const q = query(collection(db, 'farmers'), orderBy('name', 'asc'));
      const querySnapshot = await getDocs(q);
      setFarmers(querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    } catch (error) {
      console.error('Error fetching farmers:', error);
    }
  };

  const fetchFlops = async () => {
    try {
      const q = query(collection(db, 'flops'), orderBy('name', 'asc'));
      const querySnapshot = await getDocs(q);
      setFlops(querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    } catch (error) {
      console.error('Error fetching flops:', error);
    }
  };

  const fetchFilteredRecords = async () => {
    setLoading(true);
    try {
      let q = query(collection(db, 'farmRecords'), orderBy('date', 'desc'));
      
      // Apply filters
      if (selectedFarmer) {
        q = query(q, where('farmerId', '==', selectedFarmer));
      }
      if (selectedFlop) {
        q = query(q, where('flopId', '==', selectedFlop));
      }
      
      const querySnapshot = await getDocs(q);
      let recordsData = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      // Apply date filters in JavaScript since Firestore doesn't support complex date queries
      if (selectedYear) {
        recordsData = recordsData.filter(record => {
          const recordYear = new Date(record.date).getFullYear().toString();
          return recordYear === selectedYear;
        });
      }

      if (selectedMonth) {
        recordsData = recordsData.filter(record => {
          const recordMonth = (new Date(record.date).getMonth() + 1).toString().padStart(2, '0');
          return recordMonth === selectedMonth;
        });
      }

      setRecords(recordsData);
      
      // Calculate statistics
      if (recordsData.length > 0) {
        calculateStatistics(recordsData);
      } else {
        setStatistics({
          total: {},
          average: {},
          minimum: {},
          maximum: {}
        });
      }
    } catch (error) {
      console.error('Error fetching filtered records:', error);
    } finally {
      setLoading(false);
    }
  };

  const calculateStatistics = (data) => {
    const numericFields = ['tray', 'deliveredTray', 'givenAndCrackTray', 'trayStock', 'newBag', 'perDayBag', 'givenBag', 'bagStock', 'mortality', 'liveBird', 'productionPercentage', 'feedConsumption', 'birdDays'];
    
    const stats = {
      total: {},
      average: {},
      minimum: {},
      maximum: {}
    };

    numericFields.forEach(field => {
      const values = data.map(record => parseFloat(record[field]) || 0).filter(val => !isNaN(val));
      
      if (values.length > 0) {
        stats.total[field] = values.reduce((sum, val) => sum + val, 0);
        stats.average[field] = stats.total[field] / values.length;
        stats.minimum[field] = Math.min(...values);
        stats.maximum[field] = Math.max(...values);
      } else {
        stats.total[field] = 0;
        stats.average[field] = 0;
        stats.minimum[field] = 0;
        stats.maximum[field] = 0;
      }
    });

    setStatistics(stats);
  };

  const generatePDF = async () => {
    if (records.length === 0) {
      alert('No records found for the selected filters.');
      return;
    }

    setGeneratingPDF(true);
    try {
      // Load jsPDF and jsPDF-AutoTable from CDN
      await loadScript('https://cdnjs.cloudflare.com/ajax/libs/jspdf/2.5.1/jspdf.umd.min.js');
      await loadScript('https://cdnjs.cloudflare.com/ajax/libs/jspdf-autotable/3.8.1/jspdf.plugin.autotable.min.js');
      
             const { jsPDF } = window.jspdf;
       const doc = new jsPDF('landscape');
      
      // Get filter information for the title
      const selectedFarmerName = selectedFarmer ? farmers.find(f => f.id === selectedFarmer)?.name : 'All Farmers';
      const selectedFlopName = selectedFlop ? flops.find(f => f.id === selectedFlop)?.name : 'All Flops';
      const monthName = selectedMonth ? new Date(2024, parseInt(selectedMonth) - 1).toLocaleString('default', { month: 'long' }) : 'All Months';
      
             // Title
       doc.setFontSize(18);
       doc.setFont(undefined, 'bold');
       doc.text('Farm Records Report', 148, 20, { align: 'center' });
       
       // Subtitle with filters
       doc.setFontSize(11);
       doc.setFont(undefined, 'normal');
       doc.text(`Period: ${monthName} ${selectedYear}`, 148, 30, { align: 'center' });
       doc.text(`Farmer: ${selectedFarmerName}`, 148, 37, { align: 'center' });
       doc.text(`Flop: ${selectedFlopName}`, 148, 44, { align: 'center' });
       doc.text(`Total Records: ${records.length}`, 148, 51, { align: 'center' });
       
       // Generate date for report
       const reportDate = new Date().toLocaleDateString();
       doc.text(`Generated on: ${reportDate}`, 148, 58, { align: 'center' });

       // Calculate summary statistics
       const totalTray = records.reduce((sum, record) => sum + (parseFloat(record.tray) || 0), 0);
       const totalDeliveredTray = records.reduce((sum, record) => sum + (parseFloat(record.deliveredTray) || 0), 0);
       const totalMortality = records.reduce((sum, record) => sum + (parseFloat(record.mortality) || 0), 0);
       const totalNewBag = records.reduce((sum, record) => sum + (parseFloat(record.newBag) || 0), 0);
       const avgProductionPercentage = records.length > 0 ? 
         (records.reduce((sum, record) => sum + (parseFloat(record.productionPercentage) || 0), 0) / records.length).toFixed(2) : 0;
       
       // Add comprehensive statistics section
       doc.setFontSize(10);
       doc.setFont(undefined, 'bold');
       doc.text('Statistics Summary:', 15, 65);
       doc.setFont(undefined, 'normal');
       doc.setFontSize(8);
       
       // Calculate statistics for PDF
       const pdfStats = {
         total: {
           tray: totalTray,
           delivered: totalDeliveredTray,
           mortality: totalMortality,
           newBag: totalNewBag,
           production: avgProductionPercentage * records.length / 100
         },
         average: {
           tray: totalTray / records.length,
           delivered: totalDeliveredTray / records.length,
           mortality: totalMortality / records.length,
           newBag: totalNewBag / records.length,
           production: avgProductionPercentage
         },
         minimum: {
           tray: Math.min(...records.map(r => parseFloat(r.tray) || 0)),
           delivered: Math.min(...records.map(r => parseFloat(r.deliveredTray) || 0)),
           mortality: Math.min(...records.map(r => parseFloat(r.mortality) || 0)),
           newBag: Math.min(...records.map(r => parseFloat(r.newBag) || 0)),
           production: Math.min(...records.map(r => parseFloat(r.productionPercentage) || 0))
         },
         maximum: {
           tray: Math.max(...records.map(r => parseFloat(r.tray) || 0)),
           delivered: Math.max(...records.map(r => parseFloat(r.deliveredTray) || 0)),
           mortality: Math.max(...records.map(r => parseFloat(r.mortality) || 0)),
           newBag: Math.max(...records.map(r => parseFloat(r.newBag) || 0)),
           production: Math.max(...records.map(r => parseFloat(r.productionPercentage) || 0))
         }
       };
       
       // Total row
       doc.setFont(undefined, 'bold');
       doc.text('Total:', 15, 69);
       doc.setFont(undefined, 'normal');
       doc.text(`Tray: ${pdfStats.total.tray.toFixed(0)} | Delivered: ${pdfStats.total.delivered.toFixed(0)} | Mortality: ${pdfStats.total.mortality.toFixed(0)} | New Bag: ${pdfStats.total.newBag.toFixed(0)} | Production: ${pdfStats.total.production.toFixed(1)}%`, 35, 69);
       
       // Average row
       doc.setFont(undefined, 'bold');
       doc.text('Average:', 15, 72);
       doc.setFont(undefined, 'normal');
       doc.text(`Tray: ${pdfStats.average.tray.toFixed(1)} | Delivered: ${pdfStats.average.delivered.toFixed(1)} | Mortality: ${pdfStats.average.mortality.toFixed(1)} | New Bag: ${pdfStats.average.newBag.toFixed(1)} | Production: ${pdfStats.average.production.toFixed(1)}%`, 35, 72);
       
       // Min/Max row
       doc.setFont(undefined, 'bold');
       doc.text('Min/Max:', 15, 75);
       doc.setFont(undefined, 'normal');
       doc.text(`Tray: ${pdfStats.minimum.tray.toFixed(0)}/${pdfStats.maximum.tray.toFixed(0)} | Mortality: ${pdfStats.minimum.mortality.toFixed(0)}/${pdfStats.maximum.mortality.toFixed(0)} | Production: ${pdfStats.minimum.production.toFixed(1)}%/${pdfStats.maximum.production.toFixed(1)}%`, 35, 75);

             // Prepare table data with better formatting
       const tableData = records.map(record => [
         new Date(record.date).toLocaleDateString(),
         (record.farmerName || '-').substring(0, 20), // Limit farmer name length
         (record.flopName || '-').substring(0, 25), // Limit flop name length
         parseFloat(record.tray || 0).toFixed(0),
         parseFloat(record.deliveredTray || 0).toFixed(0),
         parseFloat(record.givenAndCrackTray || 0).toFixed(0),
         parseFloat(record.trayStock || 0).toFixed(0),
         parseFloat(record.newBag || 0).toFixed(0),
         parseFloat(record.perDayBag || 0).toFixed(0),
         parseFloat(record.givenBag || 0).toFixed(0),
         parseFloat(record.bagStock || 0).toFixed(0),
         parseFloat(record.mortality || 0).toFixed(0),
         parseFloat(record.liveBird || 0).toFixed(0),
         parseFloat(record.productionPercentage || 0).toFixed(1) + '%',
         parseFloat(record.feedConsumption || 0).toFixed(2) + '%',
         parseFloat(record.birdDays || 0).toFixed(0)
       ]);

                    // Create table with better column widths
       doc.autoTable({
         tableWidth: 'auto',
         head: [[
           'Date',
           'Farmer',
           'Flop',
           'Tray',
           'Delivered',
           'Given & Crack',
           'Tray Stock',
           'New Bag',
           'Per Day Bag',
           'Given Bag',
           'Bag Stock',
           'Mortality',
           'Live Bird',
           'Production %',
           'Feed Consumption %',
           'Bird Days'
         ]],
         body: tableData,
         startY: 82,
         styles: {
           fontSize: 7,
           cellPadding: 1
         },
         headStyles: {
           fillColor: [59, 130, 246],
           textColor: 255,
           fontStyle: 'bold',
           fontSize: 7,
           halign: 'center'
         },
         alternateRowStyles: {
           fillColor: [248, 250, 252]
         },
         columnStyles: {
           0: { cellWidth: 22 }, // Date
           1: { cellWidth: 28 }, // Farmer
           2: { cellWidth: 32 }, // Flop
           3: { cellWidth: 12 }, // Tray
           4: { cellWidth: 15 }, // Delivered
           5: { cellWidth: 18 }, // Given & Crack
           6: { cellWidth: 15 }, // Tray Stock
           7: { cellWidth: 12 }, // New Bag
           8: { cellWidth: 15 }, // Per Day Bag
           9: { cellWidth: 12 }, // Given Bag
           10: { cellWidth: 15 }, // Bag Stock
           11: { cellWidth: 12 }, // Mortality
           12: { cellWidth: 12 }, // Live Bird
           13: { cellWidth: 15 }, // Production %
           14: { cellWidth: 18 }, // Feed Consumption
           15: { cellWidth: 12 }  // Bird Days
         },
         margin: { top: 60, right: 3, bottom: 10, left: 3 },
         didDrawPage: function (data) {
           // Add page number
           doc.setFontSize(10);
           doc.text(`Page ${doc.internal.getNumberOfPages()}`, data.settings.margin.left, doc.internal.pageSize.height - 10);
         }
       });

      // Add comments section if any records have comments
      const recordsWithComments = records.filter(record => 
        record.fieldComments && Object.values(record.fieldComments).some(comment => comment && comment.trim() !== '')
      );

      if (recordsWithComments.length > 0) {
        let yPosition = doc.lastAutoTable.finalY + 10;
        
        // Check if we need a new page
        if (yPosition > doc.internal.pageSize.height - 50) {
          doc.addPage();
          yPosition = 20;
        }

        doc.setFontSize(14);
        doc.text('Comments Section', 105, yPosition, { align: 'center' });
        yPosition += 10;

        recordsWithComments.forEach(record => {
          const recordDate = new Date(record.date).toLocaleDateString();
          const comments = [];
          
          Object.entries(record.fieldComments || {}).forEach(([field, comment]) => {
            if (comment && comment.trim() !== '') {
              comments.push(`${field}: ${comment}`);
            }
          });

          if (comments.length > 0) {
            // Check if we need a new page
            if (yPosition > doc.internal.pageSize.height - 30) {
              doc.addPage();
              yPosition = 20;
            }

            doc.setFontSize(10);
            doc.setFont(undefined, 'bold');
            doc.text(`${recordDate} - ${record.farmerName} (${record.flopName})`, 15, yPosition);
            yPosition += 5;

            doc.setFont(undefined, 'normal');
            comments.forEach(comment => {
              // Check if we need a new page
              if (yPosition > doc.internal.pageSize.height - 20) {
                doc.addPage();
                yPosition = 20;
              }
              
              // Split long comments
              const maxWidth = 180;
              const lines = doc.splitTextToSize(comment, maxWidth);
              lines.forEach(line => {
                if (yPosition > doc.internal.pageSize.height - 20) {
                  doc.addPage();
                  yPosition = 20;
                }
                doc.text(line, 20, yPosition);
                yPosition += 5;
              });
              yPosition += 2;
            });
            yPosition += 5;
          }
        });
      }

      // Save the PDF
      const fileName = `farm_records_${selectedYear}_${selectedMonth || 'all'}_${selectedFarmerName.replace(/\s+/g, '_')}_${selectedFlopName.replace(/\s+/g, '_')}.pdf`;
      doc.save(fileName);

    } catch (error) {
      console.error('Error generating PDF:', error);
      alert('Error generating PDF. Please make sure you have an internet connection and try again.');
    } finally {
      setGeneratingPDF(false);
    }
  };

  const clearFilters = () => {
    setSelectedFarmer('');
    setSelectedFlop('');
    setSelectedMonth('');
    setSelectedYear(new Date().getFullYear().toString());
  };

  const getMonthOptions = () => {
    const months = [];
    for (let i = 1; i <= 12; i++) {
      const monthNum = i.toString().padStart(2, '0');
      const monthName = new Date(2024, i - 1).toLocaleString('default', { month: 'long' });
      months.push({ value: monthNum, label: monthName });
    }
    return months;
  };

  const getYearOptions = () => {
    const currentYear = new Date().getFullYear();
    const years = [];
    for (let i = currentYear; i >= currentYear - 5; i--) {
      years.push(i.toString());
    }
    return years;
  };

  const loadScript = (src) => {
    return new Promise((resolve, reject) => {
      if (document.querySelector(`script[src="${src}"]`)) {
        resolve();
        return;
      }
      
      const script = document.createElement('script');
      script.src = src;
      script.onload = resolve;
      script.onerror = reject;
      document.head.appendChild(script);
    });
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-4xl max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-6">
          <div>
            <h2 className="text-2xl font-bold text-gray-800">Export Farm Records</h2>
            <p className="text-sm text-gray-600 mt-1">Generate PDF reports with filtering options</p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700"
          >
            ✕
          </button>
        </div>

        {/* Filters */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <Calendar className="h-4 w-4 inline mr-1" />
              Year
            </label>
            <select
              value={selectedYear}
              onChange={(e) => setSelectedYear(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              {getYearOptions().map(year => (
                <option key={year} value={year}>{year}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <Calendar className="h-4 w-4 inline mr-1" />
              Month (Optional)
            </label>
            <select
              value={selectedMonth}
              onChange={(e) => setSelectedMonth(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">All Months</option>
              {getMonthOptions().map(month => (
                <option key={month.value} value={month.value}>{month.label}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <Users className="h-4 w-4 inline mr-1" />
              Farmer (Optional)
            </label>
            <select
              value={selectedFarmer}
              onChange={(e) => setSelectedFarmer(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">All Farmers</option>
              {farmers.map((farmer) => (
                <option key={farmer.id} value={farmer.id}>{farmer.name}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <Building className="h-4 w-4 inline mr-1" />
              Flop (Optional)
            </label>
            <select
              value={selectedFlop}
              onChange={(e) => setSelectedFlop(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">All Flops</option>
              {flops.map((flop) => (
                <option key={flop.id} value={flop.id}>{flop.name}</option>
              ))}
            </select>
          </div>
        </div>

                 {/* Statistics Section */}
         {showTable && (
           <div className="mb-6">
             <h3 className="text-lg font-medium text-gray-900 mb-4">Statistics</h3>
             <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
               {/* Total */}
               <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                 <h4 className="text-lg font-semibold text-orange-800 mb-3">Total</h4>
                 <div className="space-y-2 text-sm">
                   <div><span className="font-medium">Tray:</span> {statistics.total.tray?.toFixed(0) || 0}</div>
                   <div><span className="font-medium">Delivered:</span> {statistics.total.deliveredTray?.toFixed(0) || 0}</div>
                   <div><span className="font-medium">Mortality:</span> {statistics.total.mortality?.toFixed(0) || 0}</div>
                   <div><span className="font-medium">New Bag:</span> {statistics.total.newBag?.toFixed(0) || 0}</div>
                   <div><span className="font-medium">Production:</span> {statistics.total.productionPercentage?.toFixed(1) || 0}%</div>
                 </div>
               </div>

               {/* Average */}
               <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                 <h4 className="text-lg font-semibold text-green-800 mb-3">Average</h4>
                 <div className="space-y-2 text-sm">
                   <div><span className="font-medium">Tray:</span> {statistics.average.tray?.toFixed(1) || 0}</div>
                   <div><span className="font-medium">Delivered:</span> {statistics.average.deliveredTray?.toFixed(1) || 0}</div>
                   <div><span className="font-medium">Mortality:</span> {statistics.average.mortality?.toFixed(1) || 0}</div>
                   <div><span className="font-medium">New Bag:</span> {statistics.average.newBag?.toFixed(1) || 0}</div>
                   <div><span className="font-medium">Production:</span> {statistics.average.productionPercentage?.toFixed(1) || 0}%</div>
                 </div>
               </div>

               {/* Minimum */}
               <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                 <h4 className="text-lg font-semibold text-yellow-800 mb-3">Minimum</h4>
                 <div className="space-y-2 text-sm">
                   <div><span className="font-medium">Tray:</span> {statistics.minimum.tray?.toFixed(0) || 0}</div>
                   <div><span className="font-medium">Delivered:</span> {statistics.minimum.deliveredTray?.toFixed(0) || 0}</div>
                   <div><span className="font-medium">Mortality:</span> {statistics.minimum.mortality?.toFixed(0) || 0}</div>
                   <div><span className="font-medium">New Bag:</span> {statistics.minimum.newBag?.toFixed(0) || 0}</div>
                   <div><span className="font-medium">Production:</span> {statistics.minimum.productionPercentage?.toFixed(1) || 0}%</div>
                 </div>
               </div>

               {/* Maximum */}
               <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                 <h4 className="text-lg font-semibold text-blue-800 mb-3">Maximum</h4>
                 <div className="space-y-2 text-sm">
                   <div><span className="font-medium">Tray:</span> {statistics.maximum.tray?.toFixed(0) || 0}</div>
                   <div><span className="font-medium">Delivered:</span> {statistics.maximum.deliveredTray?.toFixed(0) || 0}</div>
                   <div><span className="font-medium">Mortality:</span> {statistics.maximum.mortality?.toFixed(0) || 0}</div>
                   <div><span className="font-medium">New Bag:</span> {statistics.maximum.newBag?.toFixed(0) || 0}</div>
                   <div><span className="font-medium">Production:</span> {statistics.maximum.productionPercentage?.toFixed(1) || 0}%</div>
                 </div>
               </div>
             </div>
           </div>
         )}

         {/* Results Summary */}
         {showTable && (
           <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mb-6">
             <div className="flex items-center justify-between mb-3">
               <div>
                 <h3 className="text-lg font-medium text-gray-900">Records Found</h3>
                 <p className="text-sm text-gray-700">
                   {loading ? 'Loading...' : `${records.length} records found for the selected filters`}
                 </p>
               </div>
               <button
                 onClick={clearFilters}
                 className="px-3 py-1 text-sm text-gray-600 hover:text-gray-800"
               >
                 Clear Filters
               </button>
             </div>
           </div>
         )}

                 {/* Preview Table */}
         {showTable && records.length > 0 && (
          <div className="mb-6">
            <h3 className="text-lg font-medium text-gray-900 mb-3">Preview (First 5 Records)</h3>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 border border-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Farmer</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Flop</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Tray</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Mortality</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Comments</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {records.slice(0, 5).map((record) => (
                    <tr key={record.id}>
                      <td className="px-3 py-2 text-sm text-gray-900">
                        {new Date(record.date).toLocaleDateString()}
                      </td>
                      <td className="px-3 py-2 text-sm text-gray-900">{record.farmerName}</td>
                      <td className="px-3 py-2 text-sm text-gray-900">{record.flopName}</td>
                      <td className="px-3 py-2 text-sm text-gray-900">{record.tray}</td>
                      <td className="px-3 py-2 text-sm text-gray-900">{record.mortality}</td>
                      <td className="px-3 py-2 text-sm text-gray-900">
                        {record.fieldComments && Object.values(record.fieldComments).some(comment => comment && comment.trim() !== '') 
                          ? 'Yes' 
                          : 'No'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* No Filters Message */}
        {!showTable && (
          <div className="text-center py-12 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
            <div className="text-gray-500">
              <h3 className="text-lg font-medium mb-2">Select Filters to View Data</h3>
              <p className="text-sm">Choose year, month, farmer, or flop to see farm records and statistics</p>
            </div>
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300"
          >
            Cancel
          </button>
          <button
            onClick={generatePDF}
            disabled={loading || records.length === 0 || generatingPDF}
            className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Download className="h-4 w-4 mr-2" />
            {generatingPDF ? 'Generating PDF...' : 'Generate PDF'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default FarmRecordsExport; 